import moment, { Moment } from "moment";

import {
  TFunction,
  calculateTotalCO2ForYear,
  calculateTotalProgramPayoutsForYear,
  getIsSubmissionPeriodOpen,
  isFieldEnrolled,
  shouldShowNextYearCropsFeatureFlag,
} from "utils";

import { FERTILIZER_BUNDLE_SLUGS, TILLAGE_SEEDING_BUNDLE_SLUGS } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  AccountingMethodology,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  CarbonProgramResult,
  CropFCPO,
  CropItemData,
  CropPracticesRequestBody,
  DataSubmissionDatesResponse,
  EDITING_REQUEST_TYPE,
  FieldListField,
  FieldOutcomesByPractice,
  FieldPracticeResponse,
  GetVerificationEditingRequestsResponse,
  ItemWithMissingData,
  ItemsWithMissingData,
  ItemsWithMissingDataByFieldId,
  PRACTICE_SLUG,
  PRACTICE_TO_BUNDLE_MAP,
  PracticeFCPO,
  PracticeFCPORequest,
  PracticeSlug,
  VerificationEditingRequest,
  ZERO_UUID,
} from "models";

import { PracticeConfigForBulk } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { getInputRequirementConfig, isRequired } from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline";
import { getCropPracticeType } from "pages/app/field-timeline/utils/CropUtils";

const MANDATORY_FUTURE_SLUGS: PracticeSlug[] = [...TILLAGE_SEEDING_BUNDLE_SLUGS, ...FERTILIZER_BUNDLE_SLUGS];

export const getExistingPracticesAppliedStatus = (practices: PracticeFCPO[]): { defaultToggleState: boolean | null; existingPractices: PracticeFCPO[] } => {
  const existingPracticesWithNoZeroId = practices.filter((practice) => practice.assignedPracticeId !== ZERO_UUID);
  const intentionallyNo = !!practices.find((practice) => practice.assignedPracticeId === ZERO_UUID);
  const intentionallyYes = existingPracticesWithNoZeroId.length > 0;

  // Note: This will be true if there is at least one practice with no ZERO_UUID
  const defaultToggleState = intentionallyYes ? true : intentionallyNo ? false : null;

  return { defaultToggleState, existingPractices: existingPracticesWithNoZeroId };
};

export const determinePracticeSettings = ({
  practiceConfigForBulk,
  practices,
  disabled,
}: {
  practices: PracticeFCPO[];
  disabled: boolean;
  practiceConfigForBulk?: PracticeConfigForBulk;
}): { defaultToggleState: boolean | null; toggleDisabled: boolean; existingPractices: PracticeFCPO[]; hasMixedValues?: boolean } => {
  if (!practiceConfigForBulk) {
    const { existingPractices, defaultToggleState } = getExistingPracticesAppliedStatus(practices);
    return { existingPractices, defaultToggleState, toggleDisabled: disabled };
  }

  const { toggleState, toggleDisabled, bulkPracticeState, practiceToShow } = practiceConfigForBulk;
  return {
    defaultToggleState: toggleState,
    existingPractices: practiceToShow,
    toggleDisabled,
    hasMixedValues: bulkPracticeState === "MIXED" || bulkPracticeState === "ONLY_ONE_CROP_WITH_PRACTICES",
  };
};

export const createCropPracticeRequestBody = (
  crop: CropFCPO,
  formPractices: PracticeFCPO[],
  isTillageApplied: boolean | null,
  isMineralApplied: boolean | null,
  isOrganicApplied: boolean | null,
  hasFertilizerManagement: boolean | null,
  existingPractices: PracticeFCPO[]
): CropPracticesRequestBody => {
  const practices: Omit<PracticeFCPORequest, "verificationStatus">[] = [];

  // Create ZERO_ID practices to indicate that the user has intentionally decided to leave out a practice (NO)
  if (isTillageApplied === false) {
    const emptyPractice = {
      startDate: moment(crop.cropType === CROP_TYPE_FCPO.MAIN ? crop.harvestEndDate : crop.seedingStartDate).toISOString(),
      endDate: moment(crop.cropType === CROP_TYPE_FCPO.MAIN ? crop.harvestEndDate : crop.seedingStartDate).toISOString(),
      type: PRACTICE_SLUG.TILLAGES,
      assignedPracticeId: ZERO_UUID,
    };
    practices.push(emptyPractice);
  }
  if (isMineralApplied === false) {
    const emptyPractice = {
      startDate: moment(crop.cropType === CROP_TYPE_FCPO.MAIN ? crop.harvestEndDate : crop.seedingStartDate).toISOString(),
      endDate: moment(crop.cropType === CROP_TYPE_FCPO.MAIN ? crop.harvestEndDate : crop.seedingStartDate).toISOString(),
      type: PRACTICE_SLUG.MINERAL_FERTILIZERS,
      assignedPracticeId: ZERO_UUID,
      amount: 0,
    };
    practices.push(emptyPractice);
  }
  if (isOrganicApplied === false) {
    const emptyPractice = {
      startDate: moment(crop.cropType === CROP_TYPE_FCPO.MAIN ? crop.harvestEndDate : crop.seedingStartDate).toISOString(),
      endDate: moment(crop.cropType === CROP_TYPE_FCPO.MAIN ? crop.harvestEndDate : crop.seedingStartDate).toISOString(),
      type: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
      assignedPracticeId: ZERO_UUID,
      amount: 0,
      applicationType: ZERO_UUID,
      nValue: 0,
      selfProduced: false,
    };
    practices.push(emptyPractice);
  }

  // Only add practices to the response body, if the user has intentionally decided to add practices (YES)
  if (isTillageApplied) {
    const tillages = formPractices.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES);
    practices.push(...tillages);
  }
  if (isMineralApplied) {
    const mineralFertilizers = formPractices.filter((practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
    practices.push(...mineralFertilizers);
  }
  if (isOrganicApplied) {
    const organicFertilizers = formPractices.filter((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);
    practices.push(...organicFertilizers);
  }

  // Inject previous verification status
  const injectedPractices: PracticeFCPORequest[] = practices.map((practice) => {
    // Find the corresponding existng practice (based on id or if it has ZERO_UUID (e.g. no fertilizer applied))
    const existingPractice = existingPractices.find(
      (existingPractice) =>
        existingPractice.type === practice.type &&
        (("id" in practice && existingPractice.id === practice.id) ||
          (practice.assignedPracticeId === ZERO_UUID && existingPractice.assignedPracticeId === ZERO_UUID))
    );

    return {
      ...practice,
      verificationStatus: existingPractice?.verificationStatus ?? null,
    };
  });

  return {
    crop,
    practices: injectedPractices,
    hasFertilizerManagement,
  };
};

// get the year for a crop based on its seeding start and harvest end dates, this should be used just for data submission purposes, not accounting
export const getCropYearFromDates = (cropType: CROP_TYPE_FCPO, seedingStartDate: Moment, harvestEndDate: Moment): number | null => {
  const cropReferenceDate = cropType === CROP_TYPE_FCPO.MAIN ? harvestEndDate : seedingStartDate;
  if (!cropReferenceDate.isValid()) return null;

  return cropReferenceDate.year();
};

export const isRelevantYear = (fieldOutcomes: FieldOutcomesByPractice, slug: PracticeSlug, year: number | undefined): boolean => {
  if (!fieldOutcomes[slug]?.years || !year) return false;
  if (year in (fieldOutcomes[slug]?.years || {})) return true;
  if (shouldShowNextYearCropsFeatureFlag() && year === moment().year() + 1) {
    const isFutureMandatorySlug = MANDATORY_FUTURE_SLUGS.includes(slug);
    const isParticipating = fieldOutcomes[slug]?.isParticipating;

    if (!isFutureMandatorySlug || !isParticipating) return false;

    // Additional validation: only return true if there are actual field outcomes for this practice
    return !!fieldOutcomes[slug]?.required && Object.values(fieldOutcomes[slug]?.required || {}).some(Boolean);
  }

  return false;
};

export const cropHasDataToConfirm = (crop: CropFCPO, isSubmissionPeriodOpen: boolean): boolean => {
  // crops can only have data to confirm during the submission period
  if (!isSubmissionPeriodOpen) return false;
  // only crops for the current year can have data to confirm, older crops entries are considered confirmed by default
  const cropYear = getCropYearFromDates(crop.cropType, moment(crop.seedingStartDate), moment(crop.harvestEndDate));
  if (cropYear !== moment().year()) return false;
  // only main and cover crops have some data tha need confirmation
  if (![CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER].includes(crop.cropType)) return false;
  // if the current date is before the harvest end date, the crop has not been harvested yet, so the user cannot confirm the data
  if (moment().isBefore(moment(crop.harvestEndDate), "day")) return false;
  if (!crop.updatedAt || !moment(crop.updatedAt).isValid()) return true;

  // if a crop has been created/updated after the harvest end date, we consider the data in it to be confirmed
  return moment(crop.updatedAt).isBefore(moment(crop.harvestEndDate));
};

const filterEnrolledFields = (fields: FieldListField[], carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse): FieldListField[] =>
  fields.filter((field) => {
    const carbonProgramResult = carbonProgramOutcomesResponse[field.id];
    const isEnrolled = isFieldEnrolled(carbonProgramResult);
    return isEnrolled;
  });

const groupItemsByFieldId = (items: TimelineCalendarItemsProps<CropItemData>[]): { [fieldId: string]: TimelineCalendarItemsProps<CropItemData>[] } =>
  items.reduce(
    (acc, item) => {
      const fieldId = item.group;
      if (!acc[fieldId]) {
        acc[fieldId] = [item];
        return acc;
      }

      acc[fieldId].push(item);
      return acc;
    },
    {} as { [fieldId: string]: TimelineCalendarItemsProps<CropItemData>[] }
  );

const getMandatoryYearsByPracticeSlug = (
  fieldOutcomes: FieldOutcomesByPractice,
  accountingMethodology: AccountingMethodology
): { [practiceSlug: string]: number[] } => {
  if (accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) {
    const getMandatoryYears = (slug: PracticeSlug) =>
      [moment().year(), ...(shouldShowNextYearCropsFeatureFlag() ? [moment().year() + 1] : [])].filter((year) => isRelevantYear(fieldOutcomes, slug, year));

    return {
      [PRACTICE_SLUG.CROPS]: getMandatoryYears(PRACTICE_SLUG.CROPS),
      [PRACTICE_SLUG.TILLAGES]: getMandatoryYears(PRACTICE_SLUG.TILLAGES),
      [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: getMandatoryYears(PRACTICE_SLUG.ORGANIC_FERTILIZERS),
      // seeding and mineral fertilizer are not supported for the inventory programs
      [PRACTICE_SLUG.MINERAL_FERTILIZERS]: [],
      [PRACTICE_SLUG.SEEDINGS]: [],
    };
  }

  // for intervention farms, we want to include all the years between the baseline and the last mandatory year
  // we can't rely solely on the carbon runner response since it can include years prior the baseline or after the last mandatory year
  const lastMandatoryYear = shouldShowNextYearCropsFeatureFlag() ? moment().year() + 1 : moment().year();
  const baselineYearsByPractice = Object.keys(PRACTICE_TO_BUNDLE_MAP).reduce(
    (acc, practice) => {
      const baselineYears = Object.entries(fieldOutcomes[practice as PracticeSlug]?.years || {})
        .filter(([_, fieldOutcome]) => fieldOutcome.isBaselineYear)
        .map(([year]) => parseInt(year));

      // fill the gaps in the sequence between the first baseline year and the last mandatory year
      const firstBaselineYear = Math.min(...baselineYears);
      const filledYears = Array.from({ length: lastMandatoryYear - firstBaselineYear + 1 }, (_, i) => firstBaselineYear + i);
      const relevantYears = filledYears.filter((year) => isRelevantYear(fieldOutcomes, practice as PracticeSlug, year));
      acc[practice] = relevantYears;

      return acc;
    },
    {} as { [practiceSlug: string]: number[] }
  );

  return baselineYearsByPractice;
};

const getMissingCropsForField = (
  fieldItems: TimelineCalendarItemsProps<CropItemData>[] | undefined,
  mandatoryYearsByPracticeSlug: { [practiceSlug: string]: number[] }
): ItemWithMissingData[] => {
  if (!mandatoryYearsByPracticeSlug || Object.values(mandatoryYearsByPracticeSlug).every((years) => years.length === 0) || !fieldItems) return [];

  // we need to check for mandatory years for crops but also mandatory years for any other practices since they can only be assigned to a crop
  const mandatoryYears = Array.from(new Set(Object.values(mandatoryYearsByPracticeSlug).flat()));
  const yearsWithMissingCrops = Array.from(mandatoryYears).filter(
    (year) =>
      // when it comes to data submission, we don't care for the reporting period, we just need to check that the farm has a main crop with the harvest date in the calendar year
      !fieldItems?.some(
        (item) =>
          item.data?.cropType === CROP_TYPE_FCPO.MAIN &&
          item.data?.cropId &&
          !item.isPlaceholder &&
          getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time) === year
      )
  );

  return fieldItems
    .filter((item) => {
      const cropYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
      if (!cropYear) return false;
      return item.data?.cropType === CROP_TYPE_FCPO.MAIN && yearsWithMissingCrops.includes(cropYear);
    })
    .map((item) => ({
      id: item.id.toString(),
      startTime: item.start_time,
    })) as ItemWithMissingData[];
};

const getCropsWithMissingPractices = (
  mainAndCoverCrops: TimelineCalendarItemsProps<CropItemData>[],
  mandatoryYearsByPracticeSlug: { [practiceSlug: string]: number[] }
): ItemWithMissingData[] =>
  mainAndCoverCrops.reduce((acc, item) => {
    const assignedPractices = item.data?.cropPractices?.practices || [];
    const itemYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
    const itemId = item.id;
    if (!itemId || !itemYear) return acc;

    const isTillageRequired = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.TILLAGES].includes(itemYear);
    const isSeedingRequired = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.SEEDINGS].includes(itemYear);
    const isMineralFertilizerRequired = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.MINERAL_FERTILIZERS].includes(itemYear);
    const isOrganicFertilizerRequired = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.ORGANIC_FERTILIZERS].includes(itemYear);

    const isMissingTillage = isTillageRequired && !assignedPractices.some((practice) => practice.type === PRACTICE_SLUG.TILLAGES);
    const isMissingSeeding = isSeedingRequired && !item.data?.cropPractices?.crops?.find((crop) => crop.id === item.id)?.seedingPracticeType;
    const isMissingMineralFertilizer =
      isMineralFertilizerRequired && !assignedPractices.some((practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
    const isMissingOrganicFertilizer =
      isOrganicFertilizerRequired && !assignedPractices.some((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);

    const hasMissingData = [isMissingTillage, isMissingSeeding, isMissingMineralFertilizer, isMissingOrganicFertilizer].some(Boolean);

    if (hasMissingData) {
      acc.push({
        id: item.id.toString(),
        startTime: item.start_time,
      });
    }

    return acc;
  }, [] as ItemWithMissingData[]);

const getPracticesWithMissingData = (
  mainAndCoverCrops: TimelineCalendarItemsProps<CropItemData>[],
  carbonProgram: CarbonProgram,
  carbonProgramOutcomes: CarbonProgramResult | null,
  isFieldEnrolled: boolean,
  mandatoryYearsByPracticeSlug: { [practiceSlug: string]: number[] }
): ItemWithMissingData[] =>
  mainAndCoverCrops
    .filter((item) => {
      const itemYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
      if (!item.id || !itemYear) return false;
      const cropFCPO = item.data.cropPractices?.crops?.find((crop) => crop.id === item.id);
      if (!cropFCPO) return false;
      const inputRequirementConfig = getInputRequirementConfig(carbonProgram, carbonProgramOutcomes, cropFCPO, isFieldEnrolled);

      // check for missing yield for crops in mandatory years
      if (
        isRequired(inputRequirementConfig?.yield) &&
        typeof cropFCPO.yieldTonsPerHa !== "number" &&
        mandatoryYearsByPracticeSlug[PRACTICE_SLUG.CROPS].includes(itemYear)
      )
        return true;

      // check for missing fertilizer management for practices in mandatory years
      if (isRequired(inputRequirementConfig?.fertilizerManagement)) {
        const cropIsInMandatoryYear = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.ORGANIC_FERTILIZERS].includes(itemYear);

        if (cropIsInMandatoryYear) {
          const hasFertilizerManagement = item.data.fertilizerManagements.some(
            (fertilizerManagement) => fertilizerManagement.fieldCropId === item.id && typeof fertilizerManagement.implements4R === "boolean"
          );
          if (!hasFertilizerManagement) return true;
        }
      }

      // check for missing tillage depth for practices in mandatory years
      if (isRequired(inputRequirementConfig?.tillageDepth)) {
        const cropIsInMandatoryYear = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.TILLAGES].includes(itemYear);
        if (!cropIsInMandatoryYear) return false;

        const relevantTillagePractices =
          item.data?.cropPractices?.practices?.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES && practice.cropId === item.id) || [];
        const hasTillageDepth = relevantTillagePractices.every(
          (practice) => practice.assignedPracticeId === ZERO_UUID || typeof practice.tillageDepth === "number"
        );
        if (!hasTillageDepth) return true;
      }

      return false;
    })
    .map((item) => ({
      id: item.id,
      startTime: item.start_time,
    }))
    .filter((item) => item.id !== undefined) as ItemWithMissingData[];

const getCropsWithDataToConfirm = (mainAndCoverCrops: TimelineCalendarItemsProps<CropItemData>[], isSubmissionPeriodOpen: boolean): ItemWithMissingData[] =>
  mainAndCoverCrops
    .filter((item) => {
      const itemYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
      if (!item.id || !itemYear) return false;
      const cropFCPO = item.data.cropPractices?.crops?.find((crop) => crop.id === item.id);
      if (!cropFCPO) return false;

      return cropHasDataToConfirm(cropFCPO, isSubmissionPeriodOpen);
    })
    .map((item) => ({
      id: item.id,
      startTime: item.start_time,
    }))
    .filter((item) => item.id !== undefined) as ItemWithMissingData[];

const getCropsWithVerificationEditingRequests = (
  fieldItems: TimelineCalendarItemsProps<CropItemData>[],
  verificationEditingRequests: GetVerificationEditingRequestsResponse | undefined
): ItemWithMissingData[] => {
  if (!fieldItems || fieldItems.length === 0 || !verificationEditingRequests || verificationEditingRequests.length === 0) return [];

  const itemsWithOpenVerificationEditingRequests = verificationEditingRequests
    // crop editing requests with requestType === EDITING_REQUEST_TYPE.CROP and fieldCropId === null represent requests to add missing crops.
    // since these crops don't exist yet, they can't be found in fieldItems and are instead
    // represented as placeholders (handled in placeholdersWithOpenVerificationEditingRequests below)
    .filter((request) => request.completedAt === null && request.requestType === EDITING_REQUEST_TYPE.CROP && request.fieldCropId !== null)
    .map((cropRequest) => {
      const crop = fieldItems.find((item) => item.id === cropRequest.fieldCropId);
      if (!crop) return;

      return {
        id: crop.id,
        startTime: crop.start_time,
      };
    });

  const placeholdersWithOpenVerificationEditingRequests = fieldItems.reduce((acc, item) => {
    if (item.isPlaceholder && item.data?.isMandatory && item.data?.isFlaggedByVerifier) {
      acc.push({
        id: item.id.toString(),
        startTime: item.start_time,
      });
    }
    return acc;
  }, [] as ItemWithMissingData[]);

  return [...itemsWithOpenVerificationEditingRequests, ...placeholdersWithOpenVerificationEditingRequests].filter(
    (item) => item !== undefined
  ) as ItemWithMissingData[];
};

export const isCoverCropMissingRedzoneException = (
  cropType: CROP_TYPE_FCPO,
  isMandatory: boolean | null | undefined,
  redzoneExceptionId: string | null | undefined
): boolean => {
  if (cropType === CROP_TYPE_FCPO.COVER) {
    if (isMandatory === null || isMandatory === undefined) return true;
    if (isMandatory === true) return false;
    if (isMandatory === false && redzoneExceptionId === null) return true;
  }
  return false;
};

const getCoverCropsWithMissingRedzoneException = (
  mainAndCoverCrops: TimelineCalendarItemsProps<CropItemData>[],
  isInRedzone: boolean
): ItemWithMissingData[] => {
  if (!isInRedzone) return [];

  const missingData: ItemWithMissingData[] = [];

  mainAndCoverCrops.forEach((item) => {
    const { cropType, redzoneExceptionId, isMandatoryCoverCrop } = item.data;
    if (isCoverCropMissingRedzoneException(cropType, isMandatoryCoverCrop, redzoneExceptionId)) {
      missingData.push({
        id: item.id.toString(),
        startTime: item.start_time,
      });
    }
  });

  return missingData;
};

export const getItemsWithMissingDataByFieldId = (
  fields: FieldListField[],
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse | undefined,
  items: TimelineCalendarItemsProps<CropItemData>[],
  carbonProgram: CarbonProgram | null,
  verificationEditingRequests: GetVerificationEditingRequestsResponse | undefined,
  dataSubmissionDates: DataSubmissionDatesResponse | undefined,
  submissionFeatureFlag?: boolean
): ItemsWithMissingDataByFieldId => {
  if (!carbonProgramOutcomesResponse || !carbonProgram) return {};

  // in the inventory program, all fields are automatically enrolled
  const enrolledFields =
    carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ? fields : filterEnrolledFields(fields, carbonProgramOutcomesResponse);
  if (enrolledFields.length === 0) return {};

  const itemsByFieldId = groupItemsByFieldId(items);

  return enrolledFields.reduce((acc, field) => {
    const carbonProgramOutcomes = carbonProgramOutcomesResponse[field.id];
    if (!carbonProgramOutcomes) return acc;

    if ("error" in carbonProgramOutcomes) {
      acc[field.id] = {
        cropsWithMissingPractices: [],
        practicesWithMissingData: [],
        missingCrops: [],
        cropsWithDataToConfirm: [],
        cropsWithVerificationEditingRequests: [],
        coverCropsWithMissingRedzoneException: [],
        hasErrors: true,
      };
      return acc;
    }

    const isSubmissionPeriodOpen = getIsSubmissionPeriodOpen(dataSubmissionDates?.submissionStartDate, dataSubmissionDates?.submissionEndDate);

    const { fieldOutcomes } = carbonProgramOutcomes;
    const mandatoryYearsByPracticeSlug = getMandatoryYearsByPracticeSlug(fieldOutcomes, carbonProgram.accountingMethodology);
    const missingCrops = getMissingCropsForField(itemsByFieldId[field.id], mandatoryYearsByPracticeSlug);

    if (!itemsByFieldId[field.id] || itemsByFieldId[field.id]?.length === 0) {
      acc[field.id] = {
        cropsWithMissingPractices: [],
        practicesWithMissingData: [],
        missingCrops,
        cropsWithDataToConfirm: [],
        cropsWithVerificationEditingRequests: [],
        coverCropsWithMissingRedzoneException: [],
        hasErrors: false,
      };
      return acc;
    }

    const mainAndCoverCrops = itemsByFieldId[field.id].filter(
      (item) => !item.isPlaceholder && (item.data?.cropType === CROP_TYPE_FCPO.MAIN || item.data?.cropType === CROP_TYPE_FCPO.COVER)
    );

    const cropsWithMissingPractices = getCropsWithMissingPractices(mainAndCoverCrops, mandatoryYearsByPracticeSlug);
    // we are mapping through enrolled fields, so we can safely pass "true" as value for "isFieldEnrolled"
    const practicesWithMissingData = getPracticesWithMissingData(mainAndCoverCrops, carbonProgram, carbonProgramOutcomes, true, mandatoryYearsByPracticeSlug);
    const cropsWithDataToConfirm = submissionFeatureFlag ? getCropsWithDataToConfirm(mainAndCoverCrops, isSubmissionPeriodOpen) : [];
    const cropsWithVerificationEditingRequests = submissionFeatureFlag
      ? getCropsWithVerificationEditingRequests(itemsByFieldId[field.id], verificationEditingRequests)
      : [];
    const coverCropsWithMissingRedzoneException = getCoverCropsWithMissingRedzoneException(mainAndCoverCrops, field.isInRedzone);

    acc[field.id] = {
      cropsWithMissingPractices,
      practicesWithMissingData,
      missingCrops,
      cropsWithDataToConfirm,
      cropsWithVerificationEditingRequests,
      coverCropsWithMissingRedzoneException,
      hasErrors: false,
    };

    return acc;
  }, {} as ItemsWithMissingDataByFieldId);
};

export const hasItemsWithMissingData = (itemsWithMissingData: ItemsWithMissingData | undefined): boolean => {
  if (!itemsWithMissingData) return false;

  return (
    itemsWithMissingData.hasErrors === true ||
    itemsWithMissingData.cropsWithMissingPractices.length > 0 ||
    itemsWithMissingData.practicesWithMissingData.length > 0 ||
    itemsWithMissingData.missingCrops.length > 0 ||
    itemsWithMissingData.cropsWithDataToConfirm.length > 0 ||
    itemsWithMissingData.cropsWithVerificationEditingRequests.length > 0 ||
    itemsWithMissingData.coverCropsWithMissingRedzoneException.length > 0
  );
};

export const getItemsWithMissingDataSortedByStartTime = (itemsWithMissingData: ItemsWithMissingData | undefined): ItemWithMissingData[] => {
  if (!itemsWithMissingData || itemsWithMissingData.hasErrors) return [];

  const uniqueItemsWithMissingData = Array.from(
    new Set([
      ...itemsWithMissingData.missingCrops,
      ...itemsWithMissingData.cropsWithMissingPractices,
      ...itemsWithMissingData.practicesWithMissingData,
      ...itemsWithMissingData.cropsWithDataToConfirm,
      ...itemsWithMissingData.cropsWithVerificationEditingRequests,
      ...itemsWithMissingData.coverCropsWithMissingRedzoneException,
    ])
  );
  return uniqueItemsWithMissingData.sort((a, b) => a.startTime.diff(b.startTime));
};

export const createCropItems = (
  fieldCropOverviewResponse: FieldPracticeResponse,
  fieldId: string,
  baselineYears: number[],
  isFieldEnrolled: boolean,
  t: TFunction
): TimelineCalendarItemsProps<CropItemData>[] =>
  fieldCropOverviewResponse.crops.map((crop) => {
    const practices = fieldCropOverviewResponse.practices.filter((practice) => practice.cropId === crop.id);
    const activePracticeBundles = fieldCropOverviewResponse.availablePracticeBundles.map((bundle) => {
      if (!bundle.active || bundle.slug === "crops") return { ...bundle, hasMissingData: false };
      if (bundle.slug === "fertilizer") {
        const hasOrganicFertilizerEntry = practices.some((practice) => practice.cropId === crop.id && practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);
        const hasMineralFertilizerEntry = practices.some((practice) => practice.cropId === crop.id && practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
        return { ...bundle, hasMissingData: !hasOrganicFertilizerEntry || !hasMineralFertilizerEntry };
      }
      if (bundle.slug === "tillage") {
        const hasTillageEntry = practices.some((practice) => practice.cropId === crop.id && practice.type === PRACTICE_SLUG.TILLAGES);
        const hasSeedingTypeEntry = !!crop.seedingPracticeType;
        return { ...bundle, hasMissingData: !hasTillageEntry || !hasSeedingTypeEntry };
      }
      return { ...bundle, hasMissingData: false };
    });

    const className = crop.cropType === CROP_TYPE_FCPO.UNDERSOWING ? "real-undersowing-crop" : crop.cropType === CROP_TYPE_FCPO.COVER ? "real-cover-crop" : "";
    const startTime = moment(crop.seedingStartDate);
    const endTime = moment(crop.harvestEndDate);
    const isBaseline = isFieldEnrolled && baselineYears.includes(endTime.year());
    const isMandatory = isFieldEnrolled && crop.cropType === CROP_TYPE_FCPO.MAIN && (isBaseline || endTime.year() === moment().year());

    return {
      id: crop.id,
      group: fieldId,
      title: crop.cropName,
      start_time: startTime,
      end_time: endTime,
      practiceType: getCropPracticeType(crop.cropType),
      canResize: "both" as const,
      className,
      data: {
        cropId: crop.cropId,
        cropName: t(`${PRACTICE_SLUG.CROPS}.${crop.translationSlug}`, { ns: "practices" }),
        fieldId,
        hasResidue: crop.hasResidue,
        year: crop.year,
        payout: crop.payout,
        cropType: crop.cropType,
        cropPractices: {
          activePracticeBundles: activePracticeBundles,
          practices: practices,
          crops: fieldCropOverviewResponse.crops,
          fieldHasFailedVerification: fieldCropOverviewResponse.fieldHasFailedVerification,
        },
        isMandatory: isMandatory,
        isBaseline: baselineYears.includes(crop.year!),
        isConfirmed: crop.isConfirmed,
        fertilizerManagements: fieldCropOverviewResponse.fertilizerManagements,
        yieldTonsPerHa: crop.yieldTonsPerHa,
        cropMixId: crop.cropMixId,
        isMandatoryCoverCrop: crop.mandatory,
        redzoneExceptionId: crop.redzoneExceptionId,
      },
    };
  });

export const isCheckboxClick = (target: HTMLElement | null): boolean =>
  !!(
    target?.closest('[data-testid*="checkbox"]') ||
    target?.closest(".ant-checkbox") ||
    target?.classList.contains("ant-checkbox") ||
    target?.classList.contains("ant-checkbox-input")
  );

export const getFieldOpenTasksCount = (itemsWithMissingData: ItemsWithMissingData, fieldVerificationEditingRequests: VerificationEditingRequest[]): number => {
  if (!itemsWithMissingData) return 0;

  return (
    (itemsWithMissingData.cropsWithMissingPractices?.length ?? 0) +
    (itemsWithMissingData.practicesWithMissingData?.length ?? 0) +
    (itemsWithMissingData.missingCrops?.length ?? 0) +
    (itemsWithMissingData.cropsWithDataToConfirm?.length ?? 0) +
    (itemsWithMissingData.coverCropsWithMissingRedzoneException?.length ?? 0) +
    (fieldVerificationEditingRequests?.filter((request) => !request.completedAt)?.length ?? 0)
  );
};

export const getFarmOpenTasksCount = (
  itemsWithMissingDataByFieldId: ItemsWithMissingDataByFieldId,
  farmVerificationEditingRequests: VerificationEditingRequest[]
): { fieldsWithOpenTasksCount: number; openTasksCount: number } => {
  if (!itemsWithMissingDataByFieldId) return { fieldsWithOpenTasksCount: 0, openTasksCount: 0 };

  const numberOfFieldsWithOpenTasks = Object.entries(itemsWithMissingDataByFieldId).reduce(
    (acc, [fieldId, itemsWithMissingData]) => {
      const fieldVerificationEditingRequests = farmVerificationEditingRequests.filter((request) => request.fieldId === fieldId);
      const fieldOpenTasksCount = getFieldOpenTasksCount(itemsWithMissingData, fieldVerificationEditingRequests);
      if (fieldOpenTasksCount > 0)
        return { fieldsWithOpenTasksCount: acc.fieldsWithOpenTasksCount + 1, openTasksCount: acc.openTasksCount + fieldOpenTasksCount };
      return acc;
    },
    { fieldsWithOpenTasksCount: 0, openTasksCount: 0 }
  );

  return numberOfFieldsWithOpenTasks;
};

export const getCropOpenTasksCount = (cropId: string, itemsWithMissingData: ItemsWithMissingData): number => {
  if (!cropId) return 0;

  const hasVerificationEditingRequests = itemsWithMissingData.cropsWithVerificationEditingRequests?.some((crop) => crop.id === cropId);
  const hasMissingPractices = itemsWithMissingData.cropsWithMissingPractices?.some((crop) => crop.id === cropId);
  const hasPracticesWithMissingData = itemsWithMissingData.practicesWithMissingData?.some((crop) => crop.id === cropId);
  const hasDataToConfirm = itemsWithMissingData.cropsWithDataToConfirm?.some((crop) => crop.id === cropId);
  const isPlaceholderWithMissingMandatoryCrop = itemsWithMissingData.missingCrops?.some((crop) => crop.id === cropId);
  const hasCoverCropsWithMissingRedzoneException = itemsWithMissingData.coverCropsWithMissingRedzoneException?.some((crop) => crop.id === cropId);

  return [
    hasVerificationEditingRequests,
    hasMissingPractices,
    hasPracticesWithMissingData,
    hasDataToConfirm,
    isPlaceholderWithMissingMandatoryCrop,
    hasCoverCropsWithMissingRedzoneException,
  ].reduce((acc, curr) => acc + (curr ? 1 : 0), 0);
};

export const hasFieldLevelVerificationEditingRequests = (
  fieldId: string,
  verificationEditingRequests: GetVerificationEditingRequestsResponse | undefined
): boolean => {
  if (!fieldId || !verificationEditingRequests || verificationEditingRequests.length === 0) return false;

  return verificationEditingRequests.some(
    (request) => request.fieldId === fieldId && request.requestType !== EDITING_REQUEST_TYPE.CROP && !request.completedAt
  );
};

export const getEnrolledFieldsCount = (
  fields: FieldListField[],
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse,
  carbonProgram: CarbonProgram | null
): number => {
  if (!carbonProgramOutcomesResponse || !fields || fields.length === 0 || !carbonProgram) return 0;

  // for farms in the inventory program, all fields are automatically considered enrolled
  if (carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) return fields.length;

  const enrolledFields = fields.filter((field) => {
    const carbonProgramResult = carbonProgramOutcomesResponse[field.id];
    if (!carbonProgramResult || "error" in carbonProgramResult) return false;
    if ("error" in carbonProgramResult) return false;
    return isFieldEnrolled(carbonProgramResult);
  });

  return enrolledFields.length;
};

export const formatPayoutsPostSubmission = ({
  carbonProgramOutcomes,
  carbonProgram,
  targetYear,
  totalPayoutMin,
  totalPayoutMax,
}: {
  carbonProgramOutcomes: CarbonProgramOutcomesResponse | null;
  carbonProgram: CarbonProgram | null;
  targetYear: number;
  totalPayoutMin: number;
  totalPayoutMax: number;
}) => {
  if (!carbonProgramOutcomes || !carbonProgram?.isEnabled || !targetYear) return null;

  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  return {
    totalPayoutMin: isInventoryProgram ? totalPayoutMin : calculateTotalProgramPayoutsForYear(carbonProgramOutcomes, targetYear).totalProgramPayout,
    totalPayoutMax: isInventoryProgram ? totalPayoutMax : calculateTotalProgramPayoutsForYear(carbonProgramOutcomes, targetYear).totalProgramPayout,
    totalCO2Min: isInventoryProgram
      ? calculateTotalCO2ForYear(carbonProgramOutcomes, targetYear).min
      : calculateTotalCO2ForYear(carbonProgramOutcomes, targetYear).avg,
    totalCO2Max: isInventoryProgram
      ? calculateTotalCO2ForYear(carbonProgramOutcomes, targetYear).max
      : calculateTotalCO2ForYear(carbonProgramOutcomes, targetYear).avg,
  };
};
